import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import { Box, Stack, Typography } from '@mui/material';
import { TreeItem } from '@mui/x-tree-view';
import type { StatusesByField } from 'business/types';
import { translateBlock } from 'i18n';
import { type CatalogSelection } from 'modules/datacatalog/types';
import type {
    CategoryWithTableOnly,
    DomainWithFields,
    TableWithFields,
    TreeField,
} from 'modules/decision/pages/Explore/components/Sentence/helpers/treeData';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { UNCLASSIFIED } from 'shared/constants';

import { countFieldsInDomain, isFieldArray } from '../helpers/domain';
import CategoryRenderer from './CategoryRenderer';
import DomainLabel from './DomainLabel';
import FieldRenderer from './FieldRenderer';

type DomainFieldsRendererProps = {
    fields: TreeField[];
    domainValue: string;
    selectedName: string | null;
    onSelect: (selection: CatalogSelection) => void;
    category?: string;
    searchQuery?: string;
    displayedStatuses?: StatusesByField;
};

const DomainFieldsRenderer = ({
    domainValue,
    fields,
    selectedName,
    onSelect,
    category = 'unclassified',
    searchQuery,
    displayedStatuses,
}: DomainFieldsRendererProps) =>
    fields.map((field) => (
        <FieldRenderer
            key={field.field}
            field={field}
            domain={domainValue}
            category={category === 'unclassified' ? 'global' : category}
            isSelected={selectedName === field.field}
            onSelect={onSelect}
            searchQuery={searchQuery}
            displayedStatuses={displayedStatuses}
        />
    ));

type DomainCategoryRendererProps = {
    domainValue: string;
    domainData: CategoryWithTableOnly | TableWithFields;
    sortedCategories: string[];
    selectedName: string | null;
    onSelect: (selection: CatalogSelection) => void;
    searchQuery?: string;
    displayedStatuses?: StatusesByField;
};

const DomainCategoryRenderer = ({
    domainValue,
    domainData,
    sortedCategories,
    selectedName,
    onSelect,
    searchQuery,
    displayedStatuses,
}: DomainCategoryRendererProps) => {
    const { t } = useTranslation();
    return sortedCategories.map((category) => {
        const categoryData = domainData[category];

        if (isFieldArray(categoryData)) {
            const categoryLabel =
                category !== 'unclassified' ? t(`dataCatalog.${category}`) : '';
            return (
                <Box key={category} sx={{ mt: 1 }}>
                    {categoryLabel ? (
                        <Stack
                            direction="row"
                            spacing={1}
                            sx={{
                                alignItems: 'center',
                                px: 1,
                                py: 0.5,
                            }}
                        >
                            <FolderOutlinedIcon color="disabled" />
                            <Typography variant="body2" color="textSecondary">
                                {categoryLabel}
                            </Typography>
                        </Stack>
                    ) : null}
                    <DomainFieldsRenderer
                        domainValue={domainValue}
                        fields={categoryData}
                        selectedName={selectedName}
                        onSelect={onSelect}
                        category={category}
                        searchQuery={searchQuery}
                        displayedStatuses={displayedStatuses}
                    />
                </Box>
            );
        }

        return (
            <CategoryRenderer
                key={category}
                domain={domainValue}
                category={category}
                shouldDisplayUnclassifiedCategory={
                    sortedCategories.length !== 1
                }
                tableData={categoryData}
                selectedName={selectedName}
                onSelect={onSelect}
                searchQuery={searchQuery}
                displayedStatuses={displayedStatuses}
            />
        );
    });
};

type DomainRendererProps = {
    domain: { value: string; label: string };
    filteredTreeData: DomainWithFields;
    selectedName: string | null;
    onSelect: (selection: CatalogSelection) => void;
    searchQuery?: string;
    displayedStatuses?: StatusesByField;
};

const DomainRenderer = ({
    domain,
    filteredTreeData,
    selectedName,
    onSelect,
    searchQuery = undefined,
    displayedStatuses,
}: DomainRendererProps) => {
    const domainData = filteredTreeData[domain.value];
    const fieldCount = countFieldsInDomain(domainData);

    const sortedCategories = useMemo(() => {
        if (Array.isArray(domainData)) return [];
        return Object.keys(domainData).sort((a, b) => {
            if (a === UNCLASSIFIED) return 1;
            if (b === UNCLASSIFIED) return -1;
            const la = translateBlock(`${domain.value}.${a}`);
            const lb = translateBlock(`${domain.value}.${b}`);
            return la.localeCompare(lb);
        });
    }, [domainData, domain.value]);

    const DomainContent = isFieldArray(domainData) ? (
        <DomainFieldsRenderer
            domainValue={domain.value}
            fields={domainData}
            selectedName={selectedName}
            onSelect={onSelect}
            searchQuery={searchQuery}
            displayedStatuses={displayedStatuses}
        />
    ) : (
        <DomainCategoryRenderer
            domainValue={domain.value}
            domainData={domainData}
            sortedCategories={sortedCategories}
            selectedName={selectedName}
            onSelect={onSelect}
            searchQuery={searchQuery}
            displayedStatuses={displayedStatuses}
        />
    );
    return (
        <TreeItem
            itemId={domain.value}
            label={
                <DomainLabel
                    domain={domain.value}
                    label={domain.label}
                    count={fieldCount}
                />
            }
            sx={{ mt: 1 }}
        >
            {DomainContent}
        </TreeItem>
    );
};

export default DomainRenderer;
